
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { initiateOAuthFlow } from '@/utils/oauthProviders';
import { toast } from 'sonner';

interface OAuthButtonsProps {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const OAuthButtons: React.FC<OAuthButtonsProps> = ({ isLoading, setIsLoading }) => {
  const handleOAuthLogin = (provider: 'google' | 'facebook' | 'apple') => {
    setIsLoading(true);
    try {
      initiateOAuthFlow(provider);
    } catch (error) {
      console.error(`${provider} login error:`, error);
      toast.error(`${provider} login failed. Please try again.`);
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="relative mt-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-hormony-beige/50"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-hormony-charcoal/70">
            Or continue with
          </span>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-3 mt-6">
        <Button
          type="button"
          variant="outline"
          className="py-3 px-4 inline-flex justify-center items-center gap-2 rounded-lg border border-hormony-beige/50 bg-white text-hormony-charcoal hover:bg-hormony-beige/20 transition-all"
          onClick={() => handleOAuthLogin('google')}
          disabled={isLoading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Google
        </Button>
        
        <Button
          type="button"
          variant="outline"
          className="py-3 px-4 inline-flex justify-center items-center gap-2 rounded-lg border border-hormony-beige/50 bg-white text-hormony-charcoal hover:bg-hormony-beige/20 transition-all"
          onClick={() => handleOAuthLogin('facebook')}
          disabled={isLoading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.63 9.25c.09-.67.12-1.36.12-1.96 0-.63-.07-1.25-.21-1.86-.74 0-1.48.13-2.16.32-.69.19-1.4.51-2.02.95-.63.45-1.15.97-1.53 1.57-.38.6-.58 1.25-.58 1.98 0 .72.2 1.38.58 1.98.38.6.9 1.13 1.53 1.57.63.45 1.33.76 2.02.95.69.19 1.42.32 2.16.32.14-.61.21-1.23.21-1.86 0-.6-.03-1.29-.12-1.96M12 4c2.21 0 4.2.9 5.65 2.35 1.45 1.45 2.35 3.44 2.35 5.65s-.9 4.2-2.35 5.65C16.2 19.1 14.21 20 12 20c-2.21 0-4.2-.9-5.65-2.35C4.9 16.2 4 14.21 4 12s.9-4.2 2.35-5.65C7.8 4.9 9.79 4 12 4z" fill="#1877F2"/>
            <path d="M12 4c-2.21 0-4.2.9-5.65 2.35C4.9 7.8 4 9.79 4 12s.9 4.2 2.35 5.65C7.8 19.1 9.79 20 12 20c2.21 0 4.2-.9 5.65-2.35C19.1 16.2 20 14.21 20 12s-.9-4.2-2.35-5.65C16.2 4.9 14.21 4 12 4zm1.03 9.29h-2.06v-2.06h2.06v2.06zm0-2.82h-2.06V8.41h2.06v2.06z" fill="#1877F2"/>
          </svg>
          Facebook
        </Button>
        
        <Button
          type="button"
          variant="outline"
          className="py-3 px-4 inline-flex justify-center items-center gap-2 rounded-lg border border-hormony-beige/50 bg-white text-hormony-charcoal hover:bg-hormony-beige/20 transition-all"
          onClick={() => handleOAuthLogin('apple')}
          disabled={isLoading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.94 5.19A4.38 4.38 0 0 0 16 2.5a4.38 4.38 0 0 0-3 1.52 4.09 4.09 0 0 0-1 2.94 3.6 3.6 0 0 0 2.94-1.77z" fill="#000"/>
            <path d="M20 10.46c-.98-.8-2.27-1.25-3.3-1.25-.71 0-1.65.28-2.66.8-.8.42-1.26.6-1.54.6-.2 0-.65-.18-1.37-.56-1.06-.56-1.98-.84-2.75-.84-1.5 0-3.02.87-4.05 2.41-.95 1.43-1.3 3.1-1.3 4.96 0 1.25.23 2.53.68 3.83.9 2.56 2.58 5.33 4.3 5.33.52 0 1.26-.31 2.03-.6.56-.2.96-.29 1.25-.29.3 0 .71.1 1.29.31.83.3 1.54.61 2.13.61 2.23 0 4.21-4.33 4.95-6.17-2.84-1.17-3.73-4.3-3.73-4.99 0-2.18 1.55-3.25 1.95-3.57-.92-1.26-2.28-1.4-2.58-1.43-.38-.05-1.15.1-2.3.65z" fill="#000"/>
          </svg>
          Apple
        </Button>
      </div>
    </>
  );
};

export default OAuthButtons;
